import type { StateCreator } from 'zustand'
import { type AgentScanParams } from '@/shared/lib/api'
import { createHeaders, API_URL, fetchSseTicket } from '@/shared/lib/api'
import type { StreamEvent, ToolCall, LogEntry } from '../../types'
import type { WorkflowsSlice } from './workflowsSlice'
import type { TelemetrySlice } from './telemetrySlice'
import type { GlobalEventEnvelope } from '@/shared/lib/api/sse'

type ExecutorName = string // Executor name from backend registry

export interface StreamingSlice {
  streamingWorkflows: Set<number>
  startStreamingAgentScan: (request: AgentScanParams, onEvent?: (event: StreamEvent) => void) => Promise<string>
  startStreamingProfileScan: (
    profileId: string,
    repositoryPath: string,
    onEvent?: (event: StreamEvent) => void
  ) => Promise<string>
  startStreamingWorkflowExecution: (
    workflowId: number,
    executor?: ExecutorName,
    onEvent?: (event: StreamEvent) => void
  ) => Promise<string>
  stopStreaming: (key: string) => void
  stopWorkflowStream: (workflowId: number) => void
  stopAllStreams: () => void
}

type CombinedDeps = TelemetrySlice & WorkflowsSlice

export const createStreamingSlice: StateCreator<
  StreamingSlice & CombinedDeps,
  [],
  [],
  StreamingSlice
> = (set, get) => ({
  streamingWorkflows: new Set(),

  startStreamingAgentScan: async (request, _onEvent) => {
    const key = `agent-scan-${Date.now()}`
    // Fire-and-forget: POST to trigger endpoint. Events arrive via global bus.
    try {
      const params = new URLSearchParams({
        prompt: request.prompt,
        repository_path: request.repository_path || '.',
        mode: request.mode || 'explore',
      })
      if (request.focus_area) params.append('focus_area', request.focus_area)
      if (request.scanner) params.append('scanner', request.scanner)
      if (request.agent_definition_id) params.append('agent_definition_id', String(request.agent_definition_id))

      await fetch(`${API_URL}/workflows/agent-scan/stream?${params.toString()}`, {
        method: 'POST',
        headers: createHeaders(),
      })
    } catch (error) {
      const state = get()
      const logEntry: LogEntry = {
        id: `log-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        agentId: 'scanner',
        level: 'ERROR',
        message: `Failed to start agent scan: ${error instanceof Error ? error.message : String(error)}`,
      }
      state.addLog(logEntry)
    }
    return key
  },

  startStreamingProfileScan: async (profileId, repositoryPath, _onEvent) => {
    const key = `profile-scan-${profileId}-${Date.now()}`
    // Fire-and-forget: POST to trigger endpoint. Events arrive via global bus.
    try {
      const params = new URLSearchParams({
        profile_id: profileId,
        repository_path: repositoryPath,
      })
      await fetch(`${API_URL}/workflows/scan/profile/stream?${params.toString()}`, {
        method: 'POST',
        headers: createHeaders(),
      })
    } catch (error) {
      const state = get()
      const logEntry: LogEntry = {
        id: `log-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        agentId: 'scanner',
        level: 'ERROR',
        message: `Failed to start profile scan: ${error instanceof Error ? error.message : String(error)}`,
      }
      state.addLog(logEntry)
    }
    return key
  },

  startStreamingWorkflowExecution: async (workflowId, executor = 'claude', _onEvent) => {
    const key = `workflow-exec-${workflowId}-${Date.now()}`
    const state = get()

    const streamingWorkflows = new Set(state.streamingWorkflows)
    streamingWorkflows.add(workflowId)
    set({ streamingWorkflows })

    // Fire-and-forget: GET to trigger endpoint with SSE ticket. Events arrive via global bus.
    try {
      const ticket = await fetchSseTicket()
      await fetch(`${API_URL}/workflows/${workflowId}/execute/stream?ticket=${encodeURIComponent(ticket)}&executor=${encodeURIComponent(executor)}&permission_mode=acceptEdits`, {
        method: 'GET',
        headers: createHeaders(),
      })
    } catch (error) {
      const logEntry: LogEntry = {
        id: `log-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        agentId: executor,
        workflowId,
        level: 'ERROR',
        message: `Failed to start workflow execution: ${error instanceof Error ? error.message : String(error)}`,
      }
      get().addLog(logEntry)
      const updated = new Set(get().streamingWorkflows)
      updated.delete(workflowId)
      set({ streamingWorkflows: updated })
    }

    return key
  },

  stopStreaming: (key: string) => {
    // With fire-and-forget, there is no client to disconnect.
    // Extract workflowId from key pattern "workflow-exec-<id>-<ts>" and send cancel.
    const workflowMatch = key.match(/^workflow-exec-(\d+)-/)
    if (workflowMatch) {
      const workflowId = parseInt(workflowMatch[1], 10)
      fetch(`${API_URL}/workflows/${workflowId}/cancel`, {
        method: 'POST',
        headers: createHeaders(),
      }).catch(() => {})
      const state = get()
      const streamingWorkflows = new Set(state.streamingWorkflows)
      streamingWorkflows.delete(workflowId)
      set({ streamingWorkflows })
    }
  },

  stopWorkflowStream: (workflowId: number) => {
    fetch(`${API_URL}/workflows/${workflowId}/cancel`, {
      method: 'POST',
      headers: createHeaders(),
    }).catch(() => {})
    const state = get()
    const streamingWorkflows = new Set(state.streamingWorkflows)
    streamingWorkflows.delete(workflowId)
    set({ streamingWorkflows })
  },

  stopAllStreams: () => {
    const state = get()
    // Cancel all known streaming workflows
    state.streamingWorkflows.forEach((workflowId) => {
      fetch(`${API_URL}/workflows/${workflowId}/cancel`, {
        method: 'POST',
        headers: createHeaders(),
      }).catch(() => {})
    })
    set({ streamingWorkflows: new Set() })
  },
})

// ─────────────────────────────────────────────────────────────────────────────
// Global bus dispatcher for workflow events
// Called by useGlobalEventStream for envelope.source === 'workflow'
// ─────────────────────────────────────────────────────────────────────────────

export function dispatchWorkflowEvent(envelope: GlobalEventEnvelope): void {
  // Lazy import to avoid circular dependency at module init time
  import('@/features/orchestration/store/orchestrationStore').then(({ useOrchestrationStore }) => {
    const state = useOrchestrationStore.getState()
    const workflowId = envelope.source_id as number
    const executor = 'claude' // default — envelope doesn't carry executor name

    const logEntry: LogEntry = {
      id: `log-${Date.now()}-${Math.random()}`,
      timestamp: (envelope.timestamp as string) || new Date().toISOString(),
      agentId: executor,
      workflowId,
      level: envelope.type === 'error' ? 'ERROR' : envelope.type === 'complete' ? 'INFO' : 'DEBUG',
      message: (envelope.content as string) || '',
    }
    state.addLog(logEntry)

    if (envelope.type === 'tool_use' && envelope.data) {
      const data = envelope.data as Record<string, unknown>
      const toolUseId = data.tool_use_id as string | undefined
      const toolCall: ToolCall = {
        id: `tool-${Date.now()}-${Math.random()}`,
        agentId: executor,
        toolId: (data.tool as string) || 'unknown',
        workflowId,
        executorName: executor,
        timestamp: (envelope.timestamp as string) || new Date().toISOString(),
        status: 'RUNNING',
        durationMs: 0,
        args: (data.input as Record<string, unknown>) || {},
        result: null,
        tool_use_id: toolUseId,
      }
      state.addToolCall(toolCall)
    }

    if (envelope.type === 'tool_result' && (envelope.data as Record<string, unknown>)?.tool_use_id) {
      const data = envelope.data as Record<string, unknown>
      const result = envelope.content as string
      const status = data.is_error ? 'FAILED' : 'COMPLETED'
      const toolUseId = data.tool_use_id as string
      const durationMs = data.duration_ms as number | undefined

      const patch: Partial<ToolCall> = {
        result,
        status,
        ...(durationMs !== undefined && { durationMs }),
      }

      const matchingCall = state.toolCalls.find((tc) => tc.tool_use_id === toolUseId)
      if (matchingCall) {
        state.updateToolCall(toolUseId, patch)
      } else {
        state.updateToolCallByWorkflow(workflowId, patch)
      }
    }

    if (envelope.type === 'complete') {
      state.refreshWorkflows()
      // Remove from streaming set — need to call set() on the slice
      // We can only do this by re-reading the store
      useOrchestrationStore.setState((s) => {
        const updated = new Set(s.streamingWorkflows)
        updated.delete(workflowId)
        return { streamingWorkflows: updated }
      })
    }

    if (envelope.type === 'workflow_created' && (envelope.data as Record<string, unknown>)?.workflow_id) {
      state.refreshWorkflows()
    }
  }).catch(() => {})
}

